/* ==========================================================================
   Rook Components — Sample Drawer ("Solicitar Amostra")
   Web Component: <rk-sample-drawer-element>
   Hidratação 'idle' — wireia os listeners assim que o navegador fica ocioso,
   prontos antes de o usuário abrir o drawer.

   Compõe rk-drawer / rk-accordion / rk-scroll-area / rk-carousel sem modificá-los.

   PORTAL: o <rk-drawer-element> interno é movido para <body> no setup. Um drawer
   aninhado numa seção fica preso pelo stacking context / overflow de algum
   ancestral — z-index não resolve. Movendo pro body ele escapa e fica acima de
   tudo. O gatilho continua funcionando (o rk-drawer wireia [data-drawer-open]
   no nível do document), então só precisamos reapontar NOSSOS listeners/refs
   pro nó portado.

   Estado de seleção 100% client-side: toggla pick-cards, mostra/esconde os
   list-cards + linhas de valor pré-renderizados, recalcula o TOTAL e, no
   submit, adiciona todas as amostras selecionadas ao carrinho de uma vez.
   ========================================================================== */

import { RkComponent } from '@rook/component';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

class RkSampleDrawer extends RkComponent {
  static hydration = 'idle';

  /** @type {Set<string>} */
  selected = new Set();

  /** @type {HTMLElement | null} */
  drawerEl = null;

  /** @type {((event: Event) => void) | null} */
  #onClick = null;

  setup() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    // Portal o drawer pro <body> p/ escapar de stacking/overflow de ancestrais.
    this.drawerEl = this.querySelector('rk-drawer-element');
    if (this.drawerEl && this.drawerEl.parentElement !== document.body) {
      document.body.appendChild(this.drawerEl);
    }

    const scope = this.drawerEl;
    if (!scope) return;

    // Refs (dentro do nó portado)
    this.emptyEl = scope.querySelector('[ref="empty"]');
    this.totalEl = scope.querySelector('[ref="total"]');
    this.submitEl = scope.querySelector('[ref="submit"]');

    // Delegação própria no nó portado (RkComponent.delegate liga em `this`,
    // que já não contém mais o drawer).
    this.#onClick = (event) => {
      const el = event.target instanceof Element ? event.target : null;
      if (!el) return;
      const toggle = el.closest('[data-action="toggle"]');
      if (toggle && scope.contains(toggle)) {
        this.onToggle(toggle);
        return;
      }
      const addAll = el.closest('[data-action="add-all"]');
      if (addAll && scope.contains(addAll)) {
        this.onAddAll(event);
      }
    };
    scope.addEventListener('click', this.#onClick);

    this.updateUI();
  }

  teardown() {
    if (this.#onClick && this.drawerEl) {
      this.drawerEl.removeEventListener('click', this.#onClick);
    }
    this.#onClick = null;
    // Remove o drawer portado p/ não orfanar no <body> em re-render do editor.
    this.drawerEl?.remove();
    this.drawerEl = null;
  }

  /** @param {Element} target - the toggle button */
  onToggle(target) {
    const id = target.getAttribute('data-sample-id');
    if (!id) return;

    if (this.selected.has(id)) {
      this.selected.delete(id);
    } else {
      this.selected.add(id);
    }

    this.updateUI();
  }

  updateUI() {
    const scope = this.drawerEl;
    if (!scope) return;

    // Pick cards (carousel) — estado visual do botão
    scope.querySelectorAll('.rk-sample-pick').forEach((pick) => {
      const id = pick.getAttribute('data-sample-id');
      const on = id ? this.selected.has(id) : false;
      pick.classList.toggle('is-selected', on);
      const btn = pick.querySelector('[data-action="toggle"]');
      if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // List cards (scroll) + linhas de valor — mostra/esconde
    scope.querySelectorAll('.rk-sample-card[data-sample-id]').forEach((card) => {
      const id = card.getAttribute('data-sample-id');
      card.toggleAttribute('hidden', !(id && this.selected.has(id)));
    });

    scope.querySelectorAll('[data-sample-line]').forEach((line) => {
      const id = line.getAttribute('data-sample-line');
      line.toggleAttribute('hidden', !(id && this.selected.has(id)));
    });

    // Estado vazio
    if (this.emptyEl) {
      this.emptyEl.toggleAttribute('hidden', this.selected.size > 0);
    }

    // Botão desabilitado quando nada selecionado
    if (this.submitEl) {
      this.submitEl.toggleAttribute('disabled', this.selected.size === 0);
    }

    this.recalcTotal();
  }

  recalcTotal() {
    const scope = this.drawerEl;
    if (!scope) return;

    let cents = 0;
    this.selected.forEach((id) => {
      const pick = scope.querySelector(`.rk-sample-pick[data-sample-id="${CSS.escape(id)}"]`);
      if (pick) cents += parseInt(pick.getAttribute('data-price') || '0', 10) || 0;
    });

    if (this.totalEl) {
      this.totalEl.textContent = formatMoney(cents, this.moneyFormat, this.currency);
    }
  }

  /** @param {Event} event */
  async onAddAll(event) {
    event.preventDefault();
    const scope = this.drawerEl;
    if (!scope || this.selected.size === 0) return;

    this.submitEl?.classList.add('is-loading');

    /** @type {{ id: number, quantity: number }[]} */
    const items = [];
    this.selected.forEach((id) => {
      const pick = scope.querySelector(`.rk-sample-pick[data-sample-id="${CSS.escape(id)}"]`);
      const variantId = pick?.getAttribute('data-variant-id');
      if (variantId) items.push({ id: Number(variantId), quantity: 1 });
    });

    if (items.length === 0) {
      this.submitEl?.classList.remove('is-loading');
      return;
    }

    // Seções do cart drawer nativo p/ re-render (mesmo padrão de product-form.js)
    const sections = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const sid = el.getAttribute('data-section-id');
      if (sid && !sections.includes(sid)) sections.push(sid);
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      const data = await response.json();

      if (data.status) {
        console.error('[rk-sample-drawer] cart add error:', data.message || data);
        this.submitEl?.classList.remove('is-loading');
        return;
      }

      let cart;
      try {
        cart = await (await fetch(`${Theme.routes.cart_url || '/cart'}.js`)).json();
      } catch (_e) {
        cart = undefined;
      }

      // Dispara o fluxo nativo: cart-items re-renderiza e o cart drawer abre
      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'rk-sample-drawer', {
          source: 'rk-sample-drawer',
          sections: data.sections,
        })
      );

      // Garante a abertura do cart drawer nativo (independe de auto-open)
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();

      // Fecha este drawer e zera a seleção
      if (this.drawerEl && typeof this.drawerEl.close === 'function') this.drawerEl.close();

      this.selected.clear();
      this.updateUI();
    } catch (error) {
      console.error('[rk-sample-drawer]', error);
    } finally {
      this.submitEl?.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('rk-sample-drawer-element')) {
  customElements.define('rk-sample-drawer-element', RkSampleDrawer);
}
